import React, { useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { Header } from '../../../components/Header';
import { Footer } from '../../../components/Footer';
import { toolsData } from '../config';
import {
  getLocalizedToolField,
  getToolStructuredData,
  useSeoMetadata,
} from '../../../utils/seo';
import styles from './index.module.scss';

const ToolDetail = () => {
  const { toolId } = useParams();
  const userId = useSelector((state) => state.global.userId);
  const dispatch = useDispatch();
  const history = useHistory();
  const { i18n } = useTranslation();
  const iframeRef = useRef(null);

  const tool = toolsData.find((t) => t.id === toolId);
  const currentLang = i18n.language?.startsWith('zh') ? 'zh' : 'en';
  const toolName = tool ? getLocalizedToolField(tool, 'name', currentLang) : '';
  const toolDescription = tool ? getLocalizedToolField(tool, 'description', currentLang) : '';
  const toolGeoSummary = tool ? getLocalizedToolField(tool?.seo, 'geoSummary', currentLang) : '';
  const toolKeywords = tool
    ? [
      ...(getLocalizedToolField(tool, 'tags', currentLang) || []),
      ...(getLocalizedToolField(tool?.seo, 'keywords', currentLang) || []),
    ]
    : [];
  const toolUseCases = tool ? getLocalizedToolField(tool?.seo, 'useCases', currentLang) || [] : [];

  useSeoMetadata({
    title: tool
      ? `${toolName} | P2T Tools`
      : currentLang === 'zh' ? '工具未找到 | P2T Tools' : 'Tool not found | P2T Tools',
    description: tool
      ? (toolGeoSummary || toolDescription)
      : currentLang === 'zh' ? '未找到请求的 P2T 工具。' : 'The requested P2T tool could not be found.',
    path: tool ? `/tools/${tool.id}` : '/tools',
    lang: currentLang,
    image: tool && typeof tool.icon === 'string' && tool.icon.startsWith('/') ? tool.icon : '/favicon.ico',
    keywords: toolKeywords,
    structuredData: tool ? getToolStructuredData(tool, currentLang) : null,
  });

  useEffect(() => {
    // Static hosts without the clean-URL rewrite can still open the independent page.
    if (tool?.standalone) {
      window.location.replace(tool.url);
      return;
    }
    if (tool?.requireAuth && !userId) {
      dispatch({
        type: 'global/setShowLogin',
        payload: true,
      });
      history.push('/tools');
    }
  }, [tool, userId, dispatch, history]);

  // 发送初始配置给 iframe
  useEffect(() => {
    const sendConfig = () => {
      if (iframeRef.current && iframeRef.current.contentWindow) {
        const theme = document.documentElement.getAttribute('data-theme') || 'dark';
        console.log('[Parent] Sending INIT_CONFIG:', { theme, lang: i18n.language });

        iframeRef.current.contentWindow.postMessage(
          {
            type: 'INIT_CONFIG',
            theme,
            lang: i18n.language,
            title: tool?.name[i18n.language] || tool?.name.zh,
          },
          '*'
        );
      }
    };

    // iframe 加载完成后发送配置
    const iframe = iframeRef.current;
    if (iframe) {
      iframe.addEventListener('load', sendConfig);
      // 也在这里立即尝试发送一次
      setTimeout(sendConfig, 500);
      return () => iframe.removeEventListener('load', sendConfig);
    }
  }, [tool, i18n.language]);

  // 监听主题变化
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const theme = document.documentElement.getAttribute('data-theme') || 'dark';
      console.log('[Parent] Theme changed to:', theme);

      if (iframeRef.current && iframeRef.current.contentWindow) {
        console.log('[Parent] Sending THEME_CHANGE:', theme);
        iframeRef.current.contentWindow.postMessage(
          {
            type: 'THEME_CHANGE',
            theme,
          },
          '*'
        );
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    return () => observer.disconnect();
  }, []);

  // 监听语言变化
  useEffect(() => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      console.log('[Parent] Sending LANG_CHANGE:', i18n.language);
      iframeRef.current.contentWindow.postMessage(
        {
          type: 'LANG_CHANGE',
          lang: i18n.language,
        },
        '*'
      );
    }
  }, [i18n.language]);

  // 监听来自 iframe 的消息
  useEffect(() => {
    const handleMessage = (event) => {
      console.log('[Parent] Received message from iframe:', event.data);

      if (event.data.type === 'REQUEST_CONFIG') {
        // iframe 请求配置
        const theme = document.documentElement.getAttribute('data-theme') || 'dark';
        console.log('[Parent] Responding to REQUEST_CONFIG with theme:', theme);

        if (iframeRef.current && iframeRef.current.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            {
              type: 'INIT_CONFIG',
              theme,
              lang: i18n.language,
              title: tool?.name[i18n.language] || tool?.name.zh,
            },
            '*'
          );
        }
      } else if (
        event.source === iframeRef.current?.contentWindow
        && event.data.type === 'HOST_SET_THEME'
        && (event.data.theme === 'light' || event.data.theme === 'dark')
      ) {
        dispatch({ type: 'global/setTheme', payload: event.data.theme });
      } else if (
        event.source === iframeRef.current?.contentWindow
        && event.data.type === 'HOST_SET_LANG'
        && (event.data.lang === 'zh' || event.data.lang === 'en')
      ) {
        i18n.changeLanguage(event.data.lang);
        dispatch({ type: 'global/setUiLanguage', payload: event.data.lang });
      } else if (event.data.type === 'TITLE_UPDATED') {
        // iframe 通知标题已更新
        console.log('Title updated:', event.data.title);
        // 可以在这里保存标题到 localStorage 或后端
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [tool, i18n.language, dispatch]);

  if (tool?.standalone) return null;

  if (!tool) {
    return (
      <div className={styles.notFound}>
        <Header />
        <div className={styles.content}>
          <h2>Tool not found</h2>
          <Button type="primary" onClick={() => history.push('/tools')}>
            Back to Tools
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className={styles.toolDetail}>
      <section className={styles.seoSummary} aria-label={`${toolName} summary`}>
        <h1>{toolName}</h1>
        <p>{toolGeoSummary || toolDescription}</p>
        {toolUseCases.length > 0 && (
          <ul>
            {toolUseCases.map((useCase) => (
              <li key={useCase}>{useCase}</li>
            ))}
          </ul>
        )}
      </section>
      <div className={styles.iframeContainer}>
        <iframe
          ref={iframeRef}
          src={tool.url}
          title={tool.name.en}
          className={styles.iframe}
        />
      </div>
    </div>
  );
};

export default ToolDetail;
